import React, { useState } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, TextInput, Switch, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackScreenProps } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { RNButton } from '@/components';
import { strings } from '@/constants/residents/strings';
import { useAddCreditCardMutation, useAddBankAccountMutation, useGenerateCardConnectTokenMutation } from '@/store/api';
import { PAYMENT_COLORS } from '@/constants/residents/mobile/paymentColors';
import { MobileScreenHeader } from '@/components/residents/payments/MobileScreenHeader';
import { mobileScreenStyles } from '@/styles/mobileScreenStyles';

type PaymentMethodType = 'card' | 'bank';
type MobileAddPaymentMethodScreenProps = StackScreenProps<RootStackParamList, 'AddPaymentMethod'>;

export const MobileAddPaymentMethodScreen: React.FC = () => {
  const navigation = useNavigation<MobileAddPaymentMethodScreenProps['navigation']>();
  const route = useRoute<MobileAddPaymentMethodScreenProps['route']>();
  const returnTo = route.params?.returnTo || 'Payments';
  
  const [methodType, setMethodType] = useState<PaymentMethodType>('card');
  
  // Card fields
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [billingZip, setBillingZip] = useState('');
  
  // Bank fields
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountType, setAccountType] = useState<'ECHK' | 'ESAV'>('ECHK');
  
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [generateToken] = useGenerateCardConnectTokenMutation();
  const [addCreditCard, { isLoading: addingCard }] = useAddCreditCardMutation();
  const [addBankAccount, { isLoading: addingBank }] = useAddBankAccountMutation();
  
  const isLoading = addingCard || addingBank;

  const validateCardFields = (): boolean => {
    if (!cardNumber || cardNumber.length < 13) {
      setError('Please enter a valid card number');
      return false;
    }
    if (!expiryDate || expiryDate.length !== 4) {
      setError('Please enter expiry date (MMYY)');
      return false;
    }
    if (!cvv || cvv.length < 3) {
      setError('Please enter CVV');
      return false;
    }
    if (!cardholderName) {
      setError('Please enter cardholder name');
      return false;
    }
    return true;
  };

  const validateBankFields = (): boolean => {
    if (!accountNumber || accountNumber.length < 4) {
      setError('Please enter a valid account number');
      return false;
    }
    if (!routingNumber || routingNumber.length !== 9) {
      setError('Please enter a valid 9-digit routing number');
      return false;
    }
    if (!accountName) {
      setError('Please enter account name');
      return false;
    }
    return true;
  };

  const handleAddCard = async () => {
    if (!validateCardFields()) return;

    setError(null);
    try {
      // Generate CardConnect token
      const tokenResponse = await generateToken({
        account: cardNumber.replace(/\s/g, ''),
        expiry: expiryDate,
        cvv: cvv,
      }).unwrap();

      const token = tokenResponse?.token;
      if (!token) {
        throw new Error('Failed to tokenize card');
      }

      // Add card with token
      await addCreditCard({
        token,
        expiry: expiryDate,
        name: cardholderName,
        isDefault,
      }).unwrap();

      navigation.navigate(returnTo as any);
    } catch (err: any) {
      console.error('Failed to add card:', err);
      setError(err?.data?.message || 'Failed to add card. Please try again.');
    }
  };

  const handleAddBank = async () => {
    if (!validateBankFields()) return;

    setError(null);
    try {
      // For bank accounts, we need to tokenize via CardConnect ACH
      // For now, we'll use the account/routing directly (in production, this should be tokenized)
      const token = `${accountNumber}-${routingNumber}`;

      await addBankAccount({
        token,
        name: accountName,
        accttype: accountType,
        isDefault,
      }).unwrap();

      navigation.navigate(returnTo as any);
    } catch (err: any) {
      console.error('Failed to add bank account:', err);
      setError(err?.data?.message || 'Failed to add bank account. Please try again.');
    }
  };

  const handleSave = () => {
    if (methodType === 'card') {
      handleAddCard();
    } else {
      handleAddBank();
    }
  };

  const formatCardNumber = (text: string) => {
    const cleaned = text.replace(/\s/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted;
  };

  return (
    <KeyboardAvoidingView 
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <View style={mobileScreenStyles.container}>
        <MobileScreenHeader title={strings.mobilePayments.addPaymentMethod} />

        <ScrollView 
          style={mobileScreenStyles.content} 
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
        {/* Method Type Selector */}
        <View style={styles.typeSelector}>
          <TouchableOpacity
            style={[styles.typeButton, methodType === 'card' && styles.typeButtonActive]}
            onPress={() => setMethodType('card')}
          >
            <Text style={[styles.typeButtonText, methodType === 'card' && styles.typeButtonTextActive]}>
              {strings.mobilePayments.addCreditCard}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeButton, methodType === 'bank' && styles.typeButtonActive]}
            onPress={() => setMethodType('bank')}
          >
            <Text style={[styles.typeButtonText, methodType === 'bank' && styles.typeButtonTextActive]}>
              {strings.mobilePayments.addBankAccount}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Card Form */}
        {methodType === 'card' && (
          <View style={styles.form}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.cardNumber}</Text>
              <TextInput
                style={styles.input}
                value={formatCardNumber(cardNumber)}
                onChangeText={(text) => setCardNumber(text.replace(/\s/g, ''))}
                keyboardType="number-pad"
                placeholder="1234 5678 9012 3456"
                placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                maxLength={19}
              />
            </View>

            <View style={styles.row}>
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.label}>{strings.mobilePayments.expiryDate}</Text>
                <TextInput
                  style={styles.input}
                  value={expiryDate}
                  onChangeText={setExpiryDate}
                  keyboardType="number-pad"
                  placeholder="MMYY"
                  placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                  maxLength={4}
                />
              </View>
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.label}>{strings.mobilePayments.cvv}</Text>
                <TextInput
                  style={styles.input}
                  value={cvv}
                  onChangeText={setCvv}
                  keyboardType="number-pad"
                  placeholder="123"
                  placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                  maxLength={4}
                  secureTextEntry
                />
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.nameOnCard}</Text>
              <TextInput
                style={styles.input}
                value={cardholderName}
                onChangeText={setCardholderName}
                placeholder="John Doe"
                placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                autoCapitalize="words"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.billingZip}</Text>
              <TextInput
                style={styles.input}
                value={billingZip}
                onChangeText={setBillingZip}
                keyboardType="number-pad"
                placeholder="12345"
                placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                maxLength={5}
              />
            </View>
          </View>
        )}

        {/* Bank Form */}
        {methodType === 'bank' && (
          <View style={styles.form}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.routingNumber}</Text>
              <TextInput
                style={styles.input}
                value={routingNumber}
                onChangeText={setRoutingNumber}
                keyboardType="number-pad"
                placeholder="123456789"
                placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                maxLength={9}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.accountNumber}</Text>
              <TextInput
                style={styles.input}
                value={accountNumber}
                onChangeText={setAccountNumber}
                keyboardType="number-pad"
                placeholder="1234567890"
                placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                secureTextEntry
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.accountType}</Text>
              <View style={styles.accountTypeSelector}>
                <TouchableOpacity
                  style={[styles.accountTypeButton, accountType === 'ECHK' && styles.accountTypeButtonActive]}
                  onPress={() => setAccountType('ECHK')}
                >
                  <Text style={[styles.accountTypeText, accountType === 'ECHK' && styles.accountTypeTextActive]}>
                    {strings.mobilePayments.checking}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.accountTypeButton, accountType === 'ESAV' && styles.accountTypeButtonActive]}
                  onPress={() => setAccountType('ESAV')}
                >
                  <Text style={[styles.accountTypeText, accountType === 'ESAV' && styles.accountTypeTextActive]}>
                    {strings.mobilePayments.savings}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>{strings.mobilePayments.nameOnAccount}</Text>
              <TextInput
                style={styles.input}
                value={accountName}
                onChangeText={setAccountName}
                placeholder="John Doe"
                placeholderTextColor={RESIDENTS_MOBILE_COLORS.TEXT_TERTIARY}
                autoCapitalize="words"
              />
            </View>
          </View>
        )}

        {/* Set as Default */}
        <View style={styles.defaultSwitch}>
          <Text style={styles.defaultLabel}>{strings.mobilePayments.setAsDefault}</Text>
          <Switch
            value={isDefault}
            onValueChange={setIsDefault}
            trackColor={{ false: PAYMENT_COLORS.GRAY_MEDIUM, true: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY }}
            thumbColor={RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY}
          />
        </View>

        {error && <Text style={styles.errorText}>{error}</Text>}

        {/* Save Button */}
        <View style={styles.actions}>
          {isLoading ? (
            <ActivityIndicator size="large" color={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY} />
          ) : (
            <RNButton
              title={strings.mobilePayments.savePaymentMethod}
              onPress={handleSave}
              variant="tertiary"
              style={styles.saveButton}
              textStyle={styles.saveButtonText}
            />
          )}
        </View>
      </ScrollView>
    </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  typeSelector: {
    flexDirection: 'row',
    margin: 16,
    backgroundColor: PAYMENT_COLORS.GRAY_MEDIUM,
    borderRadius: 9,
    padding: 2,
  },
  typeButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 7,
  },
  typeButtonActive: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  typeButtonText: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
  },
  typeButtonTextActive: {
    fontWeight: '600',
  },
  form: {
    paddingHorizontal: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  input: {
    backgroundColor: PAYMENT_COLORS.WHITE,
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    borderWidth: 1,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  accountTypeSelector: {
    flexDirection: 'row',
    gap: 12,
  },
  accountTypeButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: PAYMENT_COLORS.WHITE,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    alignItems: 'center',
  },
  accountTypeButtonActive: {
    borderColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    backgroundColor: PAYMENT_COLORS.BLUE_LIGHT,
  },
  accountTypeText: {
    fontSize: 15,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
  },
  accountTypeTextActive: {
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
  },
  defaultSwitch: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: PAYMENT_COLORS.WHITE,
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  defaultLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
  },
  errorText: {
    fontSize: 14,
    color: PAYMENT_COLORS.ERROR_RED,
    marginHorizontal: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  actions: {
    paddingHorizontal: 16,
    marginBottom: 32,
  },
  saveButton: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    paddingVertical: 14,
  },
  saveButtonText: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
    fontWeight: '600',
    fontSize: 16,
  },
});

